import * as React from 'react';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { atom, useAtom, useAtomValue } from 'jotai';
import { ArrowTopRightOnSquareIcon, DocumentCheckIcon } from '@heroicons/react/24/outline';
import { jobPositionApiDataAtom } from './job-position/job-position.atoms';

import { Button, Modal, ModalContent } from '@restworld/ui-ds-base';
import { TimelineV2 } from './legacy';
import { customImageLoader, getAssetSrc, GreenCheckMarkWithWhiteBg } from '@restworld/utils-common';

import { useSaveResearchModalForJobPosition } from './worker-entry-point/worker-entry-point-modal';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IWorkerManagedUsingKlaaryoProps {}

export const workerManagedUsingKlaaryoModalFlagAtom = atom(false);
export const workerManagedFooterMapKeyAtom = atom<'cv_applied' | 'default_applied' | 'direct_applied' | 'profile'>(
  'default_applied'
);
const WorkerManagedUsingKlaaryo: React.FunctionComponent<IWorkerManagedUsingKlaaryoProps> = () => {
  const [workerManagedUsingKlaaryoModalFlag, setWorkerManagedUsingKlaaryoModalFlagAtom] = useAtom(
    workerManagedUsingKlaaryoModalFlagAtom
  );
  const { t } = useTranslation('common');
  const { openSaveResearchModal } = useSaveResearchModalForJobPosition();
  const jpData = useAtomValue(jobPositionApiDataAtom);

  // No auto-redirect to the ATS apply URL on mount. The redirect must only
  // happen after the worker has explicitly registered + uploaded CV and then
  // clicked the "Continue on ATS" CTA below. Triggering window.open at mount
  // hijacks the JP detail page before the user has shown any intent to apply.

  const defaultFooter = React.useMemo(
    () =>
      jpData.ats_applyurl ? (
        <Button
          fullwidth
          onClick={() => {
            window.open(jpData.ats_applyurl, '_blank');
          }}
        >
          {t('continue_the_application_on_ats')}
        </Button>
      ) : (
        <Button
          onClick={() => {
            openSaveResearchModal();
            setWorkerManagedUsingKlaaryoModalFlagAtom(false);
          }}
          fullwidth
        >
          {t('receive_other_offers_similar_to_this')}
        </Button>
      ),
    [openSaveResearchModal, setWorkerManagedUsingKlaaryoModalFlagAtom, t, jpData?.ats_applyurl]
  );

  return (
    <Modal
      open={workerManagedUsingKlaaryoModalFlag}
      onOpenChange={(v) => !v && setWorkerManagedUsingKlaaryoModalFlagAtom(false)}
    >
      <ModalContent
        className="max-w-full w-full md:max-w-[640px] md:min-w-104"
        title={
          <div className="flex flex-col gap-3 justify-center items-center pb-6 px-3 lg:px-[6rem]">
            {jpData?.ats_applyurl ? (
              <div className="rounded-full bg-primary-50 p-2 flex items-center justify-center">
                <ArrowTopRightOnSquareIcon className="w-6 h-6 text-primary-700" />
              </div>
            ) : (
              <GreenCheckMarkWithWhiteBg
                {...{
                  height: 24,
                  width: 24,
                }}
              />
            )}
            <h4 className="text-center text-neutral-700 text-2xl font-bold">
              {jpData?.ats_applyurl
                ? t('ats_application_pending_completion_title')
                : t('your_application_has_been_successfully_sent')}
            </h4>
            {jpData?.ats_applyurl && (
              <p className="text-center text-neutral-500 text-sm font-normal">
                {t('ats_application_pending_completion_subtitle')}
              </p>
            )}
          </div>
        }
        footer={defaultFooter}
      >
        {jpData?.ats_applyurl ? (
          <AtsContinuationSteps />
        ) : (
          <NonAtsApplicationTimeline publishedFromAts={!!jpData?.published_from_ats} />
        )}
      </ModalContent>
    </Modal>
  );
};

// Native Restworld application path: the timeline shown after submission.
// Extracted into its own component so the `timelineSteps` useMemo only runs
// when this branch actually renders.
const NonAtsApplicationTimeline: React.FunctionComponent<{ publishedFromAts: boolean }> = ({ publishedFromAts }) => {
  const { t } = useTranslation('common');

  const timelineSteps = React.useMemo(() => {
    type Step = {
      key?: number;
      title: string;
      checked: boolean;
      showDownIcon: boolean;
      verticalLineStyle: 'solid' | 'dashed';
      customContentElement?: React.ReactNode;
    };
    const steps: Step[] = [
      {
        key: 1,
        title: t('application_received'),
        checked: true,
        showDownIcon: false,
        verticalLineStyle: 'solid',
      },
      {
        key: 2,
        customContentElement: (
          <div className="px-3 py-2 rounded-lg border border-neutral-40 flex-col justify-center items-start gap-2.5 inline-flex">
            <h5 className="text-neutral-700 text-base font-medium">{t('interview_with_our_manager')}</h5>
            <div className="relative">
              <Image
                src={getAssetSrc('/utils-assets/virtual-manager-avatar.png')}
                width={56}
                height={56}
                alt="virtual-manager-avatar"
                loader={customImageLoader}
                className="rounded-full"
              />
              <Image
                className="absolute top-[10px] left-[28px]"
                src={getAssetSrc('/utils-assets/whatsapp-img.png')}
                width={50}
                height={50}
                alt="whatsapp-icon"
                loader={customImageLoader}
              />
            </div>
            <h5 className="text-gray-500 text-base font-medium">
              {t('soon_to_be_contacted', {
                virtual_manager: 'Serena',
              })}
            </h5>
          </div>
        ),
        title: t('interview_with_our_manager'),
        checked: false,
        showDownIcon: true,
        verticalLineStyle: 'dashed',
      },
      {
        key: 3,
        title: t('send_application'),
        checked: false,
        showDownIcon: true,
        verticalLineStyle: 'dashed',
      },
      {
        key: 4,
        title: t('contact_with_the_company'),
        checked: false,
        showDownIcon: true,
        verticalLineStyle: 'dashed',
      },
    ];
    if (publishedFromAts) {
      // Remove step with key 2 and renumber the remaining ones
      return steps.filter((s) => s.key !== 2).map((s, idx) => ({ ...s, key: idx + 1 }));
    }
    return steps;
  }, [t, publishedFromAts]);

  return (
    <div className="flex flex-col items-center relative px-3 lg:px-[6rem]">
      <TimelineV2 timeLineSteps={timelineSteps} />
    </div>
  );
};

// Two-step visual for ATS jobs: explains the worker has completed the profile
// on Restworld but still needs to finalize the application on the company's
// external portal. Designed to discourage a "I'm done" misread of the modal.
const AtsContinuationSteps: React.FunctionComponent = () => {
  const { t } = useTranslation('common');

  return (
    <div className="flex flex-col gap-3 px-3 lg:px-[6rem]">
      <div className="flex items-start gap-3 p-4 rounded-lg border border-neutral-30 bg-neutral-10">
        <div className="shrink-0 rounded-full bg-green-50 p-2 flex items-center justify-center">
          <DocumentCheckIcon className="w-5 h-5 text-green-600" />
        </div>
        <div className="flex flex-col gap-1">
          <h5 className="text-neutral-700 text-base font-semibold">{t('ats_step_profile_saved_title')}</h5>
          <p className="text-neutral-500 text-sm">{t('ats_step_profile_saved_description')}</p>
        </div>
      </div>
      <div className="flex items-start gap-3 p-4 rounded-lg border border-primary-200 bg-primary-50">
        <div className="shrink-0 rounded-full bg-primary-100 p-2 flex items-center justify-center">
          <ArrowTopRightOnSquareIcon className="w-5 h-5 text-primary-700" />
        </div>
        <div className="flex flex-col gap-1">
          <h5 className="text-primary-800 text-base font-semibold">{t('ats_step_finalize_on_company_title')}</h5>
          <p className="text-primary-700 text-sm">{t('ats_step_finalize_on_company_description')}</p>
        </div>
      </div>
    </div>
  );
};

export default WorkerManagedUsingKlaaryo;
